/* eslint react/jsx-sort-props: "error" */
import React, { JSX, useCallback, useContext, useMemo, useState } from 'react';
import MuiAutocomplete, {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteProps,
  AutocompleteRenderGetTagProps,
  AutocompleteRenderOptionState,
} from '@mui/material/Autocomplete';
import { makeStyles } from 'tss-react/mui';
import {
  ThemeProvider,
  StyledEngineProvider,
  styled,
  Popper,
  PopperProps,
} from '@mui/material';
import parse from 'autosuggest-highlight/parse';
import EDHighlightedText from '../Autocomplete/Components/EDHighlightedText';
import EDIconButton from '../Buttons/IconButton/EDIconButton';
import EDTooltip from '../Tooltip/EDTooltip';
import EDIcon from '../Icon/EDIcon';
import mergeSort from '../../Utils/mergeSort';
import { AvenyThemeDark, AvenyThemeSpacing } from '../ThemeProvider/AvenyTheme';
import {
  AutocompleteHighlightChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteValue,
  FilterOptionsState,
} from '@mui/material/useAutocomplete';
import { IconType } from '../Icon/Utils/IconDefinitions';
import { toString } from 'ramda';
import { CommonPropsAndTypes } from '../_Types/StandardComponentProps';
import type {
  IEditComponentProps,
  OnChange,
  OnChangeOption,
} from '../../Components/Attributes/EditableAttribute/EditComponentTypes';
import { spacing, SpacingProps } from '@mui/system';
import { editcomponentDefaultPadding } from '../../Components/Attributes/EditableAttribute/EditableAttribute';
import { isNotSpacingProp } from '../../Utils/spacingUtils';
import { EDTextEdit } from '../TextEdit/EDTextEdit';
import { EDInputGroupContext } from '../InputGroup/EDInputGroup';
import { themeColors } from '../ThemeProvider/AvenyThemeColors';
import { MASKED_VALUE_TEXT } from '../../Features/Delsystem/_Common/Hooks/useAttributeValues';

export interface IAutoCompleteProps<
  T,
  Multiple extends boolean | undefined = undefined,
  SuggestionValue extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined
> extends IEditComponentProps<T, Multiple, SuggestionValue, FreeSolo> {
  renderTags?: (
    value: EDAutoCompleteValue<T, SuggestionValue>[],
    getTagProps: AutocompleteRenderGetTagProps
  ) => React.ReactNode;

  limitTags?: number;
  notSelectedLabel?: string;
  getLimitTagsText?: (more: number) => React.ReactNode;
  getOptionLabel?: (
    option: SuggestionValue extends true ? { value: T; name: string } : T
  ) => string;
  getOptionDisabled?: (
    option: EDAutoCompleteValue<T, SuggestionValue>
  ) => boolean;
  groupBy?: (option: EDAutoCompleteValue<T, SuggestionValue>) => string;
  onInputChange?: (
    event: React.SyntheticEvent<Element, Event>,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => void;
  ref?: React.Ref<HTMLDivElement>;
  /* Om användaren "väljer" värdet som redan är valt genom att klicka på det så triggas inte onChange,
   men man kan använda det här eventet istället för att t.ex. köra om initiering som hänger ihop med valet */
  onReselectCurrentValue?: OnChange<T, Multiple, FreeSolo>;
  getOptionSelected?: (
    option: EDAutoCompleteValue<T, SuggestionValue>,
    value: EDAutoCompleteValue<T, SuggestionValue>
  ) => boolean;
  fullWidth?: boolean;
  hideClear?: boolean;
  inputMargin?: 'dense' | 'none' | 'normal';
  addGutter?: boolean;
  multiple?: Multiple;
  inputValue?: string;
  defaultValue?: AutocompleteValue<
    SuggestionValue extends true ? { value: T; name: string } : T,
    Multiple,
    undefined,
    undefined
  >;
  isHeadlineSelection?: boolean;
  disableOpenOnFocus?: boolean;
  errorMessage?: string;
  warningMessage?: string;
  hintMessage?: string;
  disableArrowIcon?: boolean;
  maxResult?: number;
  textFieldClassName?: string;
  className?: string;
  enableFullscreen?: boolean;
  inputDarkTheme?: boolean;
  freeSolo?: boolean;
  renderAutocomplete?: (
    autoComplete: React.ReactNode,
    fullscreen?: boolean
  ) => React.ReactNode;
  getOptionMatches?: (
    inputValue: string
  ) => (
    option: EDAutoCompleteValue<T, SuggestionValue>
  ) => IOptionMatch<EDAutoCompleteValue<T, SuggestionValue>>;
  autoCompleteMatch?: (
    option: IOptionMatch<T>,
    value: string
  ) => [number, number][];
  actions?: IAutoCompleteOptionAction<T>[];
  disableAutoHighlight?: boolean;
  topMargin?: CommonPropsAndTypes.StandardSpacingType;
  /**
   * If true, each option will have a tooltip of option label.
   * When there are 200+ options, rendering tooltips can have a noticable performance cost
   */
  showOptionTooltip?: boolean;
  /**Sätter tabindex till -1 */
  disableTabbing?: boolean;
  classes?: any;
  setOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  loading?: boolean;
  popperMaxHeight?: string;
}

interface IAutoCompleteOptionAction<T> {
  id: string;
  icon: IconType | ((option: T) => IconType);
  tooltip: string | ((option: T) => string);
  onClick: (option: T) => void;
  hide?: (option: T) => boolean;
  testId?: string;
  disabled?: (option: T) => boolean;
}

export interface IOptionMatch<T> {
  option: T;
  startIndex: number;
}

export interface ISuggestion<T = string> {
  name: string;
  value: T;
  data?: any;
  visibility?: string;
  category?: string;
}
export type EDAutoCompleteValue<
  T = string,
  SuggestionValue extends boolean | undefined = undefined
> = SuggestionValue extends true ? { value: T; name: string } : T;
export interface ISection {
  name: string;
  value: string;
  kombKodbeh?: boolean;
  items: ISuggestion[];
}

const useStyles = makeStyles<
  IAutoCompleteProps<
    any,
    boolean | undefined,
    boolean | undefined,
    boolean | undefined
  >,
  'inputRoot' | 'inputAdornedStart' | 'endAdornment'
>({
  uniqId: '57ebac65-8080-495b-b6fa-79e983efb857',
})(
  (theme, _params, classes) => {
    const fixedEndAdornmentPadding =
      AvenyThemeSpacing * 3 * (_params.disableArrowIcon ? 0 : 1);

    return {
      /** MUI Styles */
      root: {
        [`& .${classes.inputRoot} .${classes.inputAdornedStart}`]: {
          paddingLeft: theme.spacing(0.5),
        },
        [`& .${classes.inputRoot} .${classes.endAdornment}`]: {
          right: 3,
        },
      },
      /** Styles applied to the Input element. */
      inputRoot: {
        paddingLeft: theme.spacing(0.5),
        '&:hover:not([disabled]):before': {
          borderBottom: `1px solid ${theme.customProperties.AutocompleteColors.underline}`,
        },
        '&[class*="MuiInput-root"] > $input:first-of-type, &[class*="MuiInput-root"] > $input':
          {
            padding: theme.spacing(2, 0, 0.5),
          },
        '&[class*="MuiOutlinedInput-root"]': {
          padding: `2px ${fixedEndAdornmentPadding}px 2px ${theme.spacing(0.5)}`,
        },
        '&&[class*="MuiOutlinedInput-root"].MuiInputBase-sizeSmall': {
          paddingTop:  _params.multiple ? theme.spacing(0.375) : 0,
          paddingBottom: _params.multiple ? theme.spacing(0.375) : 0,
          paddingLeft: theme.spacing(0.5),
          paddingRight: `${fixedEndAdornmentPadding}px`,
        },
      },
      fullWidth: {
        width: '100%',
      },
      addGutter: {
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
      },
      noClearButtonPadding: {
        paddingRight: AvenyThemeSpacing * 8 - 1,
      },
      input: {},
      form: {
        backgroundColor: theme.customProperties.AutocompleteColors.background,
      },
      label: {
        '&[class*="MuiSvgIcon-root"]': {
          color: theme.palette.common.white,
        },
      },
      onDarkBackground: {
        color: theme.palette.common.white,
        '& .MuiFormControl-root': {
          marginTop: 0,
          marginLeft: 0,
          marginBottom: 0,
        },
        '& .MuiSvgIcon-root': {
          color: theme.palette.common.white,
          marginBottom: theme.spacing(0),
          marginTop: theme.spacing(0),
        },
      },
      onDarkBackgroundFocused: {
        backgroundColor: theme.palette.common.white,
        border: `2px solid ${theme.palette.primary.main}`,
        borderRadius: theme.spacing(0.5),
        color: theme.palette.text.primary,
        '& .MuiFormControl-root': {
          marginTop: 0,
          marginLeft: 0,
          marginBottom: 0,
        },
        '&& .MuiSvgIcon-root': {
          color: 'black',
        },
      },
      labelNotOutlined: {
        transform: 'translate(8px, 16px) scale(1)',
      },
      labelShrink: {},
      endAdornment: {
        paddingRight: 4,
      },
      tag: {
        backgroundColor: theme.palette.primary.main,
        margin: '16px 0px 4px',
        height: 16,
        '& > svg': {
          width: 16,
        },
      },
      textItems: {
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        alignItems: 'center',
        height: 30,
        marginLeft: 10,
      },
      textOverFlow: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: 150,
      },
      inDialogHeader: {
        minWidth: theme.spacing(35),
      },
      background: {
        backgroundColor: theme.customProperties.MenuList.activeListBg,
        boxShadow: theme.shadows[8],
      },
      fullscreenBackground: {
        backgroundColor: theme.palette.common.white,
        boxShadow: 'none',
      },
      popperDisablePortal: {
        width: '100% !important',
        flex: 1,
        position: 'initial',
        overflowY: 'auto',
      },
      listbox: {
        maxHeight: '20px',
      },
      listboxMaxHeight: {
        maxHeight: _params.popperMaxHeight,
      },
      groupLabel: {
        backgroundColor: 'inherit',
      },
      inputFocused: {},
      labelFocused: {},
      outlined: {
        maxWidth: 'calc(100% - 5.2rem)',
      },
      inputAdornedStart: {},
      notchedOutlineHidden: {
        border: 'none',
      },
      topMarginNone: {
        marginTop: 0,
      },
      topMarginDense: {
        marginTop: theme.spacing(0.5),
      },
      topMarginNormal: {
        marginTop: theme.spacing(1),
      },
      validationTextLineMarker: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        marginLeft: 0,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        fontSize: '11px',
        cursor: 'auto',
        width: '100%',
        paddingLeft: '3px',
      },
      readOnlyStyle: {
        '& .MuiInputBase-root': {
          backgroundColor: themeColors.blackFade[1000.12],
        },
      },
    } as const;
  }
);
/** Default-metod för att hämta label för en option */
const defaultGetOptionLabel = (option: unknown): string => {
  const label: string =
    typeof option === 'string'
      ? option
      : typeof option === 'object' &&
        option !== null &&
        'name' in option &&
        typeof (option as Record<string, unknown>).name === 'string'
      ? (option as Record<string, unknown>).name as string
      : toString(option);
  return label;
};
/** Default-metod för att bestämma om en option är vald för ett visst värde  */
const defaultGetOptionSelected = (option: unknown, value: unknown) => {
  return typeof option === 'object' &&
    option !== null &&
    'value' in option &&
    typeof (option as Record<string, unknown>).value === 'string' &&
    typeof value === 'string'
    ? (option as Record<string, unknown>).value === value
    : option === value;
};
type AutocompleteType = <
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined
>(
  props: AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> &
    SpacingProps
) => JSX.Element;

const StyledAutocomplete: AutocompleteType = styled(MuiAutocomplete, {
  name: 'MuiAutoComplete',
  shouldForwardProp: isNotSpacingProp,
})(
  {
    boxSizing: 'border-box',
    display: 'inline-block',
    '& .MuiFormControl-root .MuiAutocomplete-endAdornment': {
      right: 4,
    },
  },
  spacing
) as any;

/** Variant på Popper med lite extra padding för att lämna plats till en border runt komponenten när man valt hideOutline */
const HiddenOutlineAutocompletePopper = (props: PopperProps): JSX.Element => {
  return (
    <Popper
      {...props}
      sx={{
        ...props.sx,
        paddingTop: '2px',
        paddingBottom: '2px',
      }}
    />
  );
};

/**
 * Autocomplete är en select-typ av kontroll, som ger kompletteringsförslag när användaren börjar skriva och filtrerar möjliga val utifrån texten. Finns som single, som väljer ett alternativ och multiple som ger möjlighet att lägga till flera.
 */
export const EDAutoCompleteEdit = React.forwardRef(
  <
    T,
    Multiple extends boolean | undefined = undefined,
    SuggestionValue extends boolean | undefined = undefined,
    FreeSolo extends boolean | undefined = undefined
  >(
    props: IAutoCompleteProps<T, Multiple, SuggestionValue, FreeSolo>,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const {
      getOptionMatches: getOptionMatchesProp,
      getOptionLabel: getOptionLabelProp,
      autoCompleteMatch: autoCompleteMatchProp,
      onFocus: onFocusProp,
      onBlur: onBlurProp,
      setOpen,
      onReselectCurrentValue: onReselectCurrentValueProp,
    } = props;

    const [focused, setFocused] = useState(Boolean(props.autoFocus));
    const { classes, cx } = useStyles(props, { props: props as any });
    const [hasChangedInputValue, setHasChangedInputValue] = useState(false); //om användaren har ändrat inputvalue till något annat än tom sträng, då ska vi automatvälja när man tabbar ur.

    const { ref: inputGroupRef } = useContext(EDInputGroupContext);
    const inputGroupActive = inputGroupRef != null;
    const fullscreen = false;

    const value = useMemo<
      AutocompleteValue<
        SuggestionValue extends true ? { value: T; name: string } : T,
        Multiple,
        undefined,
        undefined
      >
    >(
      () =>
        props.masked
          ? MASKED_VALUE_TEXT
          : props.suggestionValue
          ? getAutocompleteValue<T, Multiple>(
              props.baseValue,
              props.options as { value: T; name: string }[],
              props.multiple
            )
          : props.multiple
          ? props.baseValue ?? props.value ?? []
          : props.baseValue ?? props.value ?? (null as any),
      [
        props.baseValue,
        props.multiple,
        props.options,
        props.suggestionValue,
        props.value,
        props.masked,
      ]
    );

    const onChange = useCallback<
      (
        event: React.SyntheticEvent,
        value: AutocompleteValue<
          SuggestionValue extends true ? { value: T; name: string } : T,
          Multiple,
          undefined,
          FreeSolo
        >,
        reason?: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<
          SuggestionValue extends true ? { value: T; name: string } : T
        >
      ) => void
    >(
      (event, value) => {
        if ('code' in event && (event as React.KeyboardEvent).code === 'Enter') {
          event.stopPropagation();
        }
        if (props.suggestionValue) {
          if (props.multiple) {
            if (props.freeSolo) {
              //suggestionValue, multiple, freeSolo
              //value är en lista av (T eller string)
              //suggestionValue, multiple, !freeSolo
              //value är en lista av T
              const typedValue = value as AutocompleteValue<
                { value: T; name: string },
                true,
                undefined,
                true
              >;
              const outerValue = typedValue.map(v =>
                typeof v === 'string' ? v : v.value
              );
              //Inga valda värden ska vara [], inte null som Mui tillåter
              const NoOption: OnChangeOption<never, true, never> = [];
              const option: OnChangeOption<T, true, true> =
                outerValue ?? NoOption;
              (props.onChange as OnChange<T, true, true>)?.(option, event);
            } else {
              //suggestionValue, multiple, !freeSolo
              //value är en lista av T
              const typedValue = value as AutocompleteValue<
                { value: T; name: string },
                true,
                undefined,
                undefined
              >;
              const outerValue = typedValue.map(v => v.value);
              //Inga valda värden ska vara [], inte null som Mui tillåter
              const NoOption: OnChangeOption<never, true, never> = [];
              const option: OnChangeOption<T, true, false> =
                outerValue ?? NoOption;
              (props.onChange as OnChange<T, true, undefined>)?.(option, event);
            }
          } else {
            if (props.freeSolo) {
              //suggestionValue, !multiple, freeSolo
              throw new Error(
                'Not implemented !suggestionValue, !multiple, freeSolo'
              );
            } else {
              //suggestionValue, !multiple, !freeSolo
              const typedValueX = value as AutocompleteValue<
                T,
                undefined,
                undefined,
                false
              >;
              const option: OnChangeOption<T, undefined, undefined> =
                typedValueX;
              (props.onChange as OnChange<T, undefined, undefined>)?.(
                option,
                event
              );
            }
          }
        } else {
          if (props.multiple) {
            if (props.freeSolo) {
              //!suggestionValue, multiple, freeSolo
              //value är en lista av (T eller string)
              throw new Error(
                'Not implemented !suggestionValue, multiple, freeSolo'
              );
            } else {
              //!suggestionValue, multiple, !freeSolo
              //value är en lista av T
              const multiValue = value as AutocompleteValue<
                T,
                true,
                undefined,
                false
              >;
              //Inga valda värden ska vara [], inte null som Mui tillåter
              const NoOption: OnChangeOption<never, true, never> = [];
              const option: OnChangeOption<T, true, false> =
                multiValue ?? NoOption;
              (props.onChange as OnChange<T, true, undefined>)?.(option, event);
            }
          } else {
            //!suggestionValue, !multiple, !freeSolo
            const typedValue = value as AutocompleteValue<
              T,
              undefined,
              undefined,
              FreeSolo
            >;
            const option: OnChangeOption<T, undefined, FreeSolo> = typedValue;
            (props.onChange as OnChange<T, undefined, FreeSolo>)?.(
              option,
              event
            );
          }
        }
      },
      [props.freeSolo, props.multiple, props.onChange, props.suggestionValue]
    );

    const onReselectCurrentValue = useCallback<
      (event: React.SyntheticEvent) => void
    >(
      event => {
        if (value && !props.multiple)
          onReselectCurrentValueProp?.(value as any, event);
      },
      [onReselectCurrentValueProp, props.multiple, value]
    );
    const slotProps = useMemo(
      () =>
        ({
          popupIndicator: { 'data-testid': `${props.testId}-popup-indicator` },
        } as any),
      [props.testId]
    );
    const onInputChange = (
      event: React.SyntheticEvent<Element, Event>,
      value: string,
      reason: AutocompleteInputChangeReason
    ) => {
      //om användaren har ändrat value till något annat än tom sträng så ska vi välja highlightat val
      //om reason är reset så är det en "programmatic change" och då har användaren inte ändrat värdet
      if (reason !== 'reset') {
        setHasChangedInputValue(value !== '');
      }
      props.onInputChange?.(event, value, reason);
    };

    const getOptionLabel = useCallback(
      (
        option: string | EDAutoCompleteValue<T, SuggestionValue>
      ): string => {
        return getOptionLabelProp
          ? getOptionLabelProp(
              option as SuggestionValue extends true
                ? { value: T; name: string }
                : T
            )
          : defaultGetOptionLabel(option);
      },
      [getOptionLabelProp]
    );
    const getOptionMatches = useCallback(
      (inputValue: string) =>
        (
          option: EDAutoCompleteValue<T, SuggestionValue>
        ): IOptionMatch<EDAutoCompleteValue<T, SuggestionValue>> => {
          const label = getOptionLabel(option);
          const startIndex = label.toLowerCase().indexOf(inputValue);
          return {
            option: option,
            startIndex: startIndex,
          };
        },
      [getOptionLabel]
    );

    const getOptionSelected: (
      option: EDAutoCompleteValue<T, SuggestionValue>,
      value: EDAutoCompleteValue<T, SuggestionValue>
    ) => boolean = props.getOptionSelected ?? defaultGetOptionSelected;

    const optionsSort = useCallback(
      (a: IOptionMatch<unknown>, b: IOptionMatch<unknown>) => {
        if (a.startIndex === -1) {
          return 1;
        } else if (b.startIndex === -1) {
          return -1;
        }

        return a.startIndex - b.startIndex;
      },
      []
    );
    const autoCompleteFilter = useCallback(
      (
        options: EDAutoCompleteValue<T, SuggestionValue>[],
        {
          inputValue,
        }: FilterOptionsState<EDAutoCompleteValue<T, SuggestionValue>>
      ) => {
        const value: string = inputValue.toLowerCase();
        const optionMatches = options.map(
          getOptionMatchesProp?.(value) ?? getOptionMatches(value)
        );
        return mergeSort(optionMatches, optionsSort)
          .slice(0, props.maxResult ? props.maxResult : options.length)
          .reduce(
            (
              list: EDAutoCompleteValue<T, SuggestionValue>[],
              option: IOptionMatch<EDAutoCompleteValue<T, SuggestionValue>>
            ): EDAutoCompleteValue<T, SuggestionValue>[] => {
              if (option.startIndex !== -1) {
                list.push(option.option);
              }

              return list;
            },
            []
          );
      },
      [getOptionMatches, optionsSort, getOptionMatchesProp, props.maxResult]
    );

    const autoCompleteMatch = useCallback(
      (
        option: EDAutoCompleteValue<T, SuggestionValue>,
        inputValue: string
      ): [number, number][] => {
        if (inputValue === '') {
          return [];
        }
        const label = getOptionLabel(option);
        const startIndex = label.toLowerCase().indexOf(inputValue);
        if (startIndex === -1) {
          return [];
        }

        return [[startIndex, startIndex + inputValue.length]];
      },
      [getOptionLabel]
    );

    const renderOption: (
      props: React.HTMLAttributes<HTMLLIElement>,
      option: any,
      state: AutocompleteRenderOptionState
    ) => React.ReactNode = useCallback(
      ({ onClick, ...listItemProps }, option, { inputValue, selected }) => {
        const label = getOptionLabel(option);
        const matches =
          autoCompleteMatchProp?.(option, inputValue.toLowerCase()) ??
          autoCompleteMatch(option, inputValue.toLowerCase());
        let parts = parse(label, matches);
        const values = props.value;

        if (Array.isArray(values)) {
          parts = parts.map(part => {
            if (selected) {
              return { ...part, highlight: true };
            }
            return part;
          });
        }

        const optionTestId =
          typeof option === 'string'
            ? option
            : option.value ?? option.namn ?? label;

        return (
          <li
            data-testid={`${props.testId}-option-${optionTestId}`}
            {...listItemProps}
            onClick={
              selected && !!onReselectCurrentValue
                ? (...args) => {
                    onReselectCurrentValue(...args);
                    onClick?.(...args);
                  }
                : onClick
            }
          >
            <div
              className={cx({
                [classes.textItems]: props.isHeadlineSelection,
              })}
            >
              <EDTooltip
                placement={'bottom-start'}
                title={props.showOptionTooltip ? label : null}
              >
                <div
                  className={
                    props.isHeadlineSelection ? classes.textOverFlow : ''
                  }
                  key={label}
                >
                  {matches.length === 0 ? (
                    label
                  ) : (
                    <EDHighlightedText parts={parts} />
                  )}
                </div>
              </EDTooltip>

              {props.actions && (
                <span>
                  {props.actions.map(action => {
                    const hide = action.hide?.(option);
                    const disabled = action.disabled?.(option);

                    if (hide) {
                      return null;
                    }

                    return (
                      <EDIconButton
                        disabled={disabled}
                        icon={
                          typeof action.icon === 'function'
                            ? action.icon(option)
                            : action.icon
                        }
                        key={action.id}
                        margin="none"
                        onClick={event => {
                          event.stopPropagation();
                          action.onClick(option);
                        }}
                        padding="dense"
                        testId={`${optionTestId}-${action.id}`}
                        tooltip={
                          typeof action.tooltip === 'function'
                            ? action.tooltip(option)
                            : action.tooltip
                        }
                      />
                    );
                  })}
                </span>
              )}
            </div>
          </li>
        );
      },
      [
        getOptionLabel,
        autoCompleteMatchProp,
        autoCompleteMatch,
        props.value,
        props.testId,
        props.isHeadlineSelection,
        props.showOptionTooltip,
        props.actions,
        cx,
        classes.textItems,
        classes.textOverFlow,
        onReselectCurrentValue,
      ]
    );

    const onFocus: React.FocusEventHandler<HTMLDivElement> = useCallback(
      event => {
        setHasChangedInputValue(false);
        setFocused(true);
        onFocusProp?.(event);
      },
      [onFocusProp]
    );

    const onBlur: React.FocusEventHandler<HTMLDivElement> = useCallback(
      event => {
        setHasChangedInputValue(false);
        setFocused(false);
        onBlurProp?.(event);
      },
      [onBlurProp]
    );

    const onHighlightChange = (
      event: unknown,
      option: unknown,
      reason: AutocompleteHighlightChangeReason
    ) => {
      if (reason === 'keyboard') {
        setHasChangedInputValue(true);
      }
    };

    const onOpen = useCallback(() => {
      setOpen?.(true);
    }, [setOpen]);

    const onClose = useCallback(() => {
      setOpen?.(false);
    }, [setOpen]);

    const autocompleteComponent = (
      <StyledAutocomplete<
        SuggestionValue extends true ? { value: T; name: string } : T,
        Multiple,
        undefined,
        any
      >
        PopperComponent={
          props.hideOutline ? HiddenOutlineAutocompletePopper : Popper
        }
        autoComplete
        autoHighlight={hasChangedInputValue && !props.disableAutoHighlight}
        autoSelect={hasChangedInputValue}
        className={props.className}
        classes={{
          inputRoot: cx(classes.inputRoot, {
            [classes.inDialogHeader]: props.isHeadlineSelection,
            [classes.noClearButtonPadding]: props.hideClear,
          }),
          endAdornment: cx(classes.endAdornment, {}),
          tag: classes.tag,
          root: cx(classes.root, {
            [classes.fullWidth]: props.fullWidth || fullscreen,
            [classes.addGutter]: props.addGutter && !fullscreen,
            [classes.onDarkBackgroundFocused]:
              props.onDarkBackground && focused,
            [classes.onDarkBackground]: props.onDarkBackground,
            [classes.readOnlyStyle]: props.readOnly,
          }),
          paper: cx({
            [classes.background]: !fullscreen,
            [classes.fullscreenBackground]: fullscreen,
          }),
          popperDisablePortal: classes.popperDisablePortal,
          listbox: cx(classes.listboxMaxHeight, {
            [classes.listbox]: fullscreen,
          }),
          groupLabel: classes.groupLabel,
        }}
        clearIcon={<EDIcon color="action" iconType="close" size="medium" />}
        clearText="Rensa"
        closeText=""
        data-testid={props.testId}
        defaultValue={props.defaultValue}
        disableClearable={props.hideClear as any}
        disabled={props.disabled || props.masked}
        filterOptions={autoCompleteFilter}
        forcePopupIcon={!props.disableArrowIcon}
        freeSolo={props.freeSolo}
        fullWidth={props.fullWidth}
        getLimitTagsText={props.getLimitTagsText}
        getOptionDisabled={props.getOptionDisabled}
        getOptionLabel={getOptionLabel}
        groupBy={props.groupBy}
        id={props.testId}
        inputValue={props.inputValue}
        isOptionEqualToValue={getOptionSelected}
        limitTags={props.limitTags ?? -1}
        loading={props.loading}
        multiple={props.multiple}
        onBlur={onBlur}
        onChange={onChange}
        onClose={onClose}
        onFocus={onFocus}
        onHighlightChange={onHighlightChange}
        onInputChange={onInputChange}
        onOpen={onOpen}
        openOnFocus={
          inputGroupActive
            ? !props.value
            : props.disableOpenOnFocus
            ? false
            : true
        }
        openText=""
        options={props.options ?? []}
        padding={props.padding ?? editcomponentDefaultPadding}
        paddingLeft={props.paddingLeft}
        paddingRight={props.paddingRight}
        readOnly={props.readOnly}
        ref={ref}
        renderInput={params => {
          const input = (
            <EDTextEdit
              {...params}
              absolutePostionedEndAdornment
              allMargins={props.inputMargin ?? props.allMargins ?? 'normal'}
              autoFocus={props.autoFocus}
              endAdornment={params.InputProps.endAdornment}
              error={Boolean(props.errorMessage)}
              fullWidth={props.fullWidth}
              helperText={
                props.required &&
                !(params.inputProps as any).value &&
                !props.errorMessage &&
                !props.warningMessage
                  ? props.requiredMessage ?? 'Värde krävs'
                  : !focused
                  ? props.errorMessage
                    ? props.errorMessage
                    : props.warningMessage
                    ? props.warningMessage
                    : props.hintMessage
                    ? props.hintMessage
                    : undefined
                  : undefined
              }
              hideClearIcon
              hideOutline={props.hideOutline}
              hoverEndAdornmentsInProp={props.hideClear ? 0 : 1}
              inputBaseClassName={params.InputProps.className}
              inputBaseRef={params.InputProps.ref}
              inputProps={{
                ...props.inputProps,
                ...params.inputProps,
                onMouseDown: params.inputProps.onMouseDown,
                onFocus: params.inputProps.onFocus,
                tabIndex: props.disableTabbing ? -1 : 0,
                style: {
                  ...params.inputProps.style,
                  color: props.onDarkBackground
                    ? focused
                      ? 'black'
                      : 'white'
                    : undefined,
                },
              }}
              inputRef={props.inputRef}
              label={
                props.hideLabel
                  ? undefined
                  : props.value
                  ? props.label
                  : props.notSelectedLabel
                  ? props.notSelectedLabel
                  : props.label
              }
              margin={props.margin}
              marginBottom={props.marginBottom}
              marginLeft={props.marginLeft}
              marginRight={props.marginRight}
              marginTop={props.marginTop}
              marginX={props.marginX}
              marginY={props.marginY}
              marker={props.marker}
              masked={props.masked}
              multiselectPadding={props.multiple}
              onDarkBackground={props.onDarkBackground}
              padding={props.padding}
              paddingBottom={props.paddingBottom}
              paddingLeft={props.paddingLeft}
              paddingRight={props.paddingRight}
              paddingTop={props.paddingTop}
              paddingX={props.paddingX}
              paddingY={props.paddingY}
              placeholder={props.placeholder}
              required={props.required}
              size={props.size}
              startAdornment={params.InputProps.startAdornment}
              staticLabel={
                props.staticLabel || !!params.InputProps.startAdornment
              }
              testId={`${props.testId}-input`}
              totalEndAdornmentsInProp={
                (props.hideClear ? 0 : 1) + (props.disableArrowIcon ? 0 : 1)
              }
              value={params.inputProps.value}
              warning={Boolean(props.warningMessage)}
              width={props.width}
            />
          );

          if (props.inputDarkTheme && !fullscreen) {
            return (
              <StyledEngineProvider injectFirst>
                <ThemeProvider theme={AvenyThemeDark}>{input}</ThemeProvider>
              </StyledEngineProvider>
            );
          }

          return input;
        }}
        renderOption={renderOption}
        renderTags={props.renderTags}
        slotProps={slotProps}
        value={value}
      />
    );

    const autocomplete = props.renderAutocomplete
      ? props.renderAutocomplete(autocompleteComponent, fullscreen)
      : autocompleteComponent;

    return <>{autocomplete}</>;
  }
);

function getAutocompleteValue<T, Multiple>(
  value: T | T[] | null | undefined,
  options: Array<{ value: T; name: string }>,
  multiple: Multiple | undefined
): AutocompleteValue<
  { value: T; name: string },
  Multiple,
  undefined,
  undefined
> {
  if (multiple) {
    let multipleValue: AutocompleteValue<
      { value: T; name: string },
      true,
      undefined,
      undefined
    > = [];
    if (Array.isArray(value)) {
      multipleValue = options.filter(option => value.includes(option.value));
    }
    return multipleValue as AutocompleteValue<
      { value: T; name: string },
      Multiple,
      undefined,
      undefined
    >;
  }
  let singleValue: AutocompleteValue<
    { value: T; name: string },
    false,
    undefined,
    undefined
  > = null;
  singleValue = options.find(option => option.value === value) ?? null;
  return singleValue as AutocompleteValue<
    { value: T; name: string },
    Multiple,
    undefined,
    undefined
  >;
}
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
function EDAutoCompleteEditComp<T>(
  props: IEditComponentProps<T>,
  ref: React.Ref<HTMLDivElement>
): JSX.Element {
  return <EDAutoCompleteEdit {...props} ref={ref} />;
}
